/**
 * ProseMirror/Tiptap schema for the WYSIWYG editor over the Canonical Document Model (Part 1.2 of
 * the WYSIWYG editor plan). See fromCdm.ts/toCdm.ts for the conversion logic and
 * roundtrip.contract.test.ts for the fidelity guarantee this schema exists to support.
 *
 * Design principle: attrs ARE the CDM model, not a lossy decomposition of it. Rather than mapping
 * RunFormat/ParagraphFormat/CellFormat/TableBorders onto a pile of narrow Tiptap marks/attrs (one
 * per property, the way NewTextTemplatePage.tsx's plain HTML/Markdown editor does), each of those
 * value objects is stored as a single attr holding the whole object. This makes the converters
 * close to structural reshaping rather than semantic translation, which is what makes an exact
 * round trip tractable at all for a maximal document. The trade-off: the editor's own toolbar (a
 * later PR) reads/writes into these attr blobs directly instead of using Tiptap's stock
 * bold/italic/etc. commands.
 *
 * `doc` is overridden to `section+` (CanonicalDocument.Sections) — DocumentProperties, Footnotes,
 * Endnotes and Monikers are NOT part of the ProseMirror document; they are editor-level state
 * threaded through fromCdm/toCdm's own top-level functions instead (Footnote/Endnote bodies reuse
 * the same block/inline conversion as section content, just outside the main `doc`).
 */
import { Node, Mark, mergeAttributes, InputRule, PasteRule, type Extensions } from '@tiptap/core'
import { placeholderLabel } from './formatters'
import Document from '@tiptap/extension-document'
import TiptapText from '@tiptap/extension-text'
import HardBreak from '@tiptap/extension-hard-break'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import { UndoRedo } from '@tiptap/extensions'
import TiptapImage from '@tiptap/extension-image'
import { NodeSelection, type EditorState, type Transaction } from '@tiptap/pm/state'
import { Table as TiptapTable } from '@tiptap/extension-table'
import TiptapTableRow from '@tiptap/extension-table-row'
import TiptapTableCell from '@tiptap/extension-table-cell'
import TiptapTableHeader from '@tiptap/extension-table-header'
import {
  type RunFormat, type ParagraphFormat, type CellFormat, type TableBorders,
  type PageSetup, type HeaderFooter, type FieldType, type FormatterSpec, type SourceLocation,
  type DocBlock, type DocInline, type ConditionalRun, type LoopRun, type TableColumnLoop, type ListNumberFormat, ListNumberFormat as ListNumberFormatEnum,
  type RowHeightRule, RowHeightRule as RowHeightRuleEnum,
  type BorderSide, type TextBoxWrap, TextBoxWrap as TextBoxWrapEnum, type FloatingAnchor,
  type CommentAnchor, CommentAnchor as CommentAnchorEnum,
  type ContentControl, type ContentControlAnchor, ContentControlAnchor as ContentControlAnchorEnum,
  type Revision, type PropertiesChange, type RunFormat as RunFormatType,
  ContentControlType as ContentControlTypeEnum, ContentControlLock as ContentControlLockEnum,
  type TableAlignment, TableAlignment as TableAlignmentEnum,
  type TableWidth, type TableLayout, TableLayout as TableLayoutEnum,
  type VAlign, VAlign as VAlignEnum, BorderStyle,
  defaultRunFormat, defaultParagraphFormat, defaultCellFormat, defaultTableBorders,
  type NoteSetup, defaultFootnoteSetup, defaultEndnoteSetup,
  defaultPageSetup, defaultSourceLocation,
} from './types'
import { runFormatToStyle, paragraphFormatToStyle, cellFormatToStyle } from './formatToStyle'
import { normalizeMonikerKey } from './monikerKey'

// ============ doc / section ============

export const CdmDocument = Document.extend({ content: 'section+' })

export interface SectionAttrs {
  page: PageSetup
  header: HeaderFooter | null
  footer: HeaderFooter | null
  firstPageHeader: HeaderFooter | null
  firstPageFooter: HeaderFooter | null
  evenPageHeader: HeaderFooter | null
  evenPageFooter: HeaderFooter | null
  footnoteSetup: NoteSetup
  endnoteSetup: NoteSetup
}

// Deliberately NOT `group: 'block'` — CDM's DocSection.Body is List<DocBlock> and never itself
// contains a nested DocSection (only CanonicalDocument.Sections, a top-level list, does), and
// `doc`'s `section+` content expression above already matches Section by NODE NAME, not group
// membership, so `group: 'block'` buys nothing structurally. It was actively harmful: it made
// `section` a candidate filler for every `block+`/`block*` expression, including Section's own
// (self-referential, since Section requires block+ too) — and because Section is registered
// before Paragraph in cdmExtensions below, ProseMirror's content-fill search tried Section first
// when synthesizing an empty document (e.g. `useEditor` with no `content` option, before an
// async-loaded document arrives), recursing into itself forever and blowing the call stack
// (`RangeError: Maximum call stack size exceeded` in `_NodeType.createAndFill`/`fillBefore`).
// `Loop`/`Conditional` below are ALSO self-referential through the block group (legitimately —
// real templates do nest loops/conditionals), but harmlessly, since Paragraph is tried first and
// terminates the search before their recursive branches are ever explored.
export const Section = Node.create({
  name: 'section',
  content: 'block+',
  isolating: true,
  addAttributes() {
    // `rendered: false` on every attr here: without it, Tiptap's default renderHTML behavior
    // dumps each one into the DOM as a literal HTML attribute via String(value) — for an object
    // this is the literal text "[object Object]", pointless and wasteful (and, at real-document
    // scale, a real amount of dead weight in the DOM for no benefit, since nothing ever parses
    // these back — content only ever comes from fromCdm.ts's structured JSON, never from HTML).
    return {
      page: { default: defaultPageSetup(), rendered: false },
      header: { default: null, rendered: false },
      footer: { default: null, rendered: false },
      firstPageHeader: { default: null, rendered: false },
      firstPageFooter: { default: null, rendered: false },
      evenPageHeader: { default: null, rendered: false },
      evenPageFooter: { default: null, rendered: false },
      // Schema v20: the section's note numbering, held like the header/footer attrs so an editor
      // round trip cannot drop it.
      footnoteSetup: { default: defaultFootnoteSetup(), rendered: false },
      endnoteSetup: { default: defaultEndnoteSetup(), rendered: false },
    }
  },
  parseHTML() {
    return [{ tag: 'section[data-cdm-section]' }]
  },
  // The section's own page size and margins as CSS variables. TemplateEditorPage's page canvas
  // (`.cdm-page`) lays each section out at its own width from them, so a landscape or label-sized
  // section is not previewed at the first section's width; the header/footer and note editors,
  // which reuse this node, do not apply them.
  renderHTML({ node, HTMLAttributes }) {
    const page = (node.attrs.page as PageSetup | undefined) ?? defaultPageSetup()
    const px = (pt: number) => `${Math.round(pt * PT_TO_PX)}px`
    const style = `--cdm-page-w:${px(page.widthPt)};--cdm-mt:${px(page.margins.top)};--cdm-mr:${px(page.margins.right)};--cdm-mb:${px(page.margins.bottom)};--cdm-ml:${px(page.margins.left)}`
    return ['section', mergeAttributes(HTMLAttributes, { 'data-cdm-section': '', style }), 0]
  },
})

// ============ Text (base, carries the runFormat mark) ============

export const Text = TiptapText

// ============ paragraph / heading ============

export interface ParagraphAttrs {
  format: ParagraphFormat
}

export const Paragraph = Node.create({
  name: 'paragraph',
  group: 'block',
  content: 'inline*',
  addAttributes() {
    return { format: { default: defaultParagraphFormat(), rendered: false } }
  },
  parseHTML() {
    return [{ tag: 'p' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['p', mergeAttributes(HTMLAttributes, { style: paragraphFormatToStyle(node.attrs.format as ParagraphFormat) }), 0]
  },
})

export interface HeadingAttrs {
  level: number
  format: ParagraphFormat
}

export const Heading = Node.create({
  name: 'heading',
  group: 'block',
  content: 'inline*',
  addAttributes() {
    return {
      level: { default: 1 },
      format: { default: defaultParagraphFormat(), rendered: false },
    }
  },
  parseHTML() {
    return [1, 2, 3, 4, 5, 6].map(level => ({ tag: `h${level}`, attrs: { level } }))
  },
  renderHTML({ node, HTMLAttributes }) {
    const level = (node.attrs.level as number) || 1
    return [`h${Math.min(Math.max(level, 1), 6)}`, mergeAttributes(HTMLAttributes, { style: paragraphFormatToStyle(node.attrs.format as ParagraphFormat) }), 0]
  },
})

// ============ list / listItem (flat CDM Level, not PM nesting) ============
//
// CDM's ListBlock is a single node holding a flat array of ListItems, each with its own 0-based
// nesting Level and one shared Ordered flag (plus an optional StartAt restart override) for the
// whole list — unlike ProseMirror's usual bulletList/orderedList + listItem, which nest
// structurally and have no notion of a bare "level" int. Modeling `list`/`listItem` this way (flat,
// level-as-attr) rather than forcing CDM's shape into PM's nested one avoids a lossy
// nested<->flat conversion in both fromCdm and toCdm. Each item's own NumberFormat travels on the
// listItem node itself (not the list), since a nested sub-level can legitimately use a different
// numbering style than its parent — see ListItem's own doc comment in types.ts.

export interface ListAttrs {
  ordered: boolean
  /** Overrides where numbering restarts (Word's w:startOverride) — null is the default of 1. */
  startAt: number | null
}

export const CdmList = Node.create({
  name: 'list',
  group: 'block',
  content: 'listItem+',
  addAttributes() {
    return { ordered: { default: false }, startAt: { default: null } }
  },
  parseHTML() {
    return [{ tag: 'ol[data-cdm-list]', attrs: { ordered: true } }, { tag: 'ul[data-cdm-list]', attrs: { ordered: false } }]
  },
  renderHTML({ node, HTMLAttributes }) {
    const tag = node.attrs.ordered ? 'ol' : 'ul'
    return [tag, mergeAttributes(HTMLAttributes, { 'data-cdm-list': '' }), 0]
  },
})

export interface ListItemAttrs {
  level: number
  /** This item's own numbering style — only meaningful when the enclosing list's ordered is true. */
  numberFormat: ListNumberFormat
}

export const CdmListItem = Node.create({
  name: 'listItem',
  content: 'block+',
  addAttributes() {
    return { level: { default: 0 }, numberFormat: { default: ListNumberFormatEnum.Decimal } }
  },
  parseHTML() {
    return [{ tag: 'li[data-cdm-list-item]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['li', mergeAttributes(HTMLAttributes, { 'data-cdm-list-item': '' }), 0]
  },
})

// ============ table / tableRow / tableCell / tableHeader ============
//
// Reuses Tiptap's real table implementation (the same one NewTextTemplatePage.tsx's plain
// HTML/Markdown editor already uses) for its editing UX (merge/split/add/delete row/column) and
// its native colspan/rowspan handling — a merged-away cell is simply absent from the PM doc, the
// same way CDM's own IsMergedContinuation cells carry no real content; toCdm re-synthesizes those
// continuation cells from colspan/rowspan on export, matching how DocxCanonicalParser's own
// ParseTable already reconstructs CDM's IsMergedContinuation shape from OOXML's vMerge/gridSpan.
// TableRow.IsHeader is a CDM row-level flag; Tiptap's table marks header-ness per CELL (tableCell
// vs tableHeader node type). The mapping used throughout is deterministic: a CDM row with
// IsHeader=true becomes all-tableHeader cells, IsHeader=false becomes all-tableCell — Tiptap's
// finer-grained per-cell flexibility is simply never reached through this schema.

export interface TableAttrs {
  columnWidthsPt: number[] | null
  borders: TableBorders
  alignment: TableAlignment
  width: TableWidth | null
  indentPt: number | null
  layout: TableLayout
  cellSpacingPt: number | null
}

export const CdmTable = TiptapTable.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      columnWidthsPt: { default: null, rendered: false },
      borders: { default: defaultTableBorders(), rendered: false },
      // Table-level properties (schema v17). rendered:false throughout: these drive the output
      // formats, not the canvas, which lays every table out full-width regardless. `width`
      // defaults to null rather than Auto because null specifically means "written before v17" —
      // see TableBlock.width.
      alignment: { default: TableAlignmentEnum.Left, rendered: false },
      width: { default: null, rendered: false },
      indentPt: { default: null, rendered: false },
      layout: { default: TableLayoutEnum.Auto, rendered: false },
      cellSpacingPt: { default: null, rendered: false },
      // Schema v29: a pending change to the table's own properties (w:tblPrChange), preserved as
      // XML. Round-tripped like every other rendered:false attr — dropping it here would accept
      // the reviewer's change on the editor's first save.
      propertiesChange: { default: null as PropertiesChange | null, rendered: false },
    }
  },
})

export interface TableRowAttrs {
  heightPt: number | null
  heightRule: RowHeightRule
  cantSplit: boolean
  control: ContentControl | null
}

// Tiptap's stock table row carries no attributes, so CDM row height / keep-together would be
// dropped on every editor round trip. They are held as plain attrs (rendered:false — they drive
// output formats, not the canvas) rather than an _extra bag, so toCdm can restore them exactly.
export const CdmTableRow = TiptapTableRow.extend({
  addAttributes() {
    return {
      heightPt: { default: null, rendered: false },
      heightRule: { default: RowHeightRuleEnum.Auto, rendered: false },
      cantSplit: { default: false, rendered: false },
      // Schema v19: the row's own content control, kept the same way — a w:sdtRow dropped on an
      // editor round trip would take the row's identity with it.
      control: { default: null as ContentControl | null, rendered: false },
      // Schema v23: the tracked change that added or removed this row.
      revision: { default: null as Revision | null, rendered: false },
    }
  },
})

export interface TableCellAttrs {
  columnLoop: TableColumnLoop | null
  colspan: number
  rowspan: number
  format: CellFormat
  control: ContentControl | null
  propertiesChange: PropertiesChange | null
}

/**
 * Delegates to the parent extension's own renderHTML (which already handles colspan/rowspan/
 * colwidth) rather than replacing it outright, and just injects our own computed `style` into
 * whatever attrs it produced — preserves the native table extension's own DOM output.
 */
function withCellStyle(base: unknown, style: string, loop?: TableColumnLoop | null): [string, Record<string, unknown>, number] {
  const [tag, attrs, content] = base as [string, Record<string, unknown>, number]
  if (!loop) return [tag, { ...attrs, style }, content]
  // A column loop's defining cells (schema v35): marked so the author can see which columns repeat.
  return [tag, {
    ...attrs,
    style: `${style};box-shadow:inset 0 3px 0 rgba(13,148,136,0.7)`,
    'data-cdm-column-loop': loop.rawName,
    title: `Repeats for each item of ${loop.rawName} ({{#columns ${loop.rawName}}})`,
  }, content]
}

export const CdmTableCell = TiptapTableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      format: { default: defaultCellFormat(), rendered: false },
      control: { default: null as ContentControl | null, rendered: false },
      // Schema v29: w:tcPrChange, same treatment as the table's own.
      propertiesChange: { default: null as PropertiesChange | null, rendered: false },
      // Schema v35: the column loop this cell's columns belong to; kept verbatim.
      columnLoop: { default: null as TableColumnLoop | null, rendered: false },
    }
  },
  renderHTML(props) {
    const style = cellFormatToStyle((props.node.attrs.format as CellFormat | undefined) ?? defaultCellFormat())
    return withCellStyle(this.parent?.(props), style, props.node.attrs.columnLoop as TableColumnLoop | null)
  },
})

export const CdmTableHeader = TiptapTableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      format: { default: defaultCellFormat(), rendered: false },
      control: { default: null as ContentControl | null, rendered: false },
      // Schema v29: w:tcPrChange, same treatment as the table's own.
      propertiesChange: { default: null as PropertiesChange | null, rendered: false },
      // Schema v35: the column loop this cell's columns belong to; kept verbatim.
      columnLoop: { default: null as TableColumnLoop | null, rendered: false },
    }
  },
  renderHTML(props) {
    const style = cellFormatToStyle((props.node.attrs.format as CellFormat | undefined) ?? defaultCellFormat())
    return withCellStyle(this.parent?.(props), style, props.node.attrs.columnLoop as TableColumnLoop | null)
  },
})

// ============ image ============

export interface ImageAttrs {
  blobRef: string
  widthPt: number
  heightPt: number
  altText: string | null
  sourceMoniker: string | null
  resolvedSource: string | null
  /** Schema v28 — see ImageBlock.wrap. Changed only by makeImageInline. */
  wrap: TextBoxWrap
  /** Schema v31 — see ImageBlock.anchor. Carried untouched; cleared only by makeImageInline. */
  anchor: FloatingAnchor | null
  /** Schema v32 — see ImageBlock.svgBlobRef. Carried untouched; the canvas shows the PNG. */
  svgBlobRef: string | null
}

const PT_TO_PX = 4 / 3 // 96dpi / 72pt — matches TemplateEditorPage.tsx's own page-canvas conversion
const SVG_NS = 'http://www.w3.org/2000/svg'

/** The field an image is bound to (`dyn:` blobRef or sourceMoniker), or null for a stored asset. */
export function dynamicImageMoniker(attrs: Partial<ImageAttrs>): string | null {
  if (attrs.sourceMoniker) return attrs.sourceMoniker
  if (typeof attrs.blobRef === 'string' && attrs.blobRef.startsWith('dyn:')) return attrs.blobRef.slice(4)
  return null
}

export const CdmImage = TiptapImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      blobRef: { default: '', rendered: false },
      widthPt: { default: 72 },
      heightPt: { default: 72 },
      sourceMoniker: { default: null, rendered: false },
      resolvedSource: { default: null, rendered: false },
      // Round-tripped, not rendered: dropping it here would destroy a floating picture's wrap
      // mode on the first save, which is the whole reason schema v28 models it.
      wrap: { default: TextBoxWrapEnum.Inline as TextBoxWrap, rendered: false },
      // Schema v31: the floating picture's position. Same reason — the editor shows the picture in
      // the flow, but a save must not turn it inline.
      anchor: { default: null as FloatingAnchor | null, rendered: false },
      // Schema v32: an SVG picture's vector original. Never fetched or shown here — the canvas
      // draws the PNG — but dropping it would lose the SVG on the first save.
      svgBlobRef: { default: null as string | null, rendered: false },
    }
  },
  renderHTML({ node, HTMLAttributes }) {
    // ImageBlock's real size is WidthPt/HeightPt (points); the base extension's own width/height
    // attrs are unused here (never set from fromCdm.ts) so a raw pixel value never leaks in as a
    // point value — sized here instead, at render time, off the CDM-native fields.
    const widthPt = node.attrs.widthPt as number | undefined
    const heightPt = node.attrs.heightPt as number | undefined
    const style = widthPt && heightPt ? `width:${Math.round(widthPt * PT_TO_PX)}px;height:${Math.round(heightPt * PT_TO_PX)}px` : undefined
    // A floating picture shows in the flow here; the attribute drives a small "Floating" marker
    // (TemplateEditorPage's stylesheet) so the author knows the output places it elsewhere.
    const floating = isFloatingImage(node.attrs as Partial<ImageAttrs>)
      ? { 'data-cdm-floating': '', title: 'Floating picture: its position and text wrap are kept on save. Select it and choose "Make inline" to place it in the text.' }
      : {}
    const moniker = dynamicImageMoniker(node.attrs as Partial<ImageAttrs>)
    if (moniker !== null) {
      // Bound to a field, so there is nothing to show until render time: a sized stand-in instead.
      return ['div', mergeAttributes(this.options.HTMLAttributes, { 'data-cdm-dyn-image': '', role: 'img', 'aria-label': `Image from {{${moniker}}}`, style }, floating),
        [`${SVG_NS} svg`, { viewBox: '0 0 24 24', width: '20', height: '20', 'aria-hidden': 'true' },
          ['rect', { x: '3', y: '4', width: '18', height: '16', rx: '2', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.5' }],
          ['circle', { cx: '9', cy: '10', r: '2', fill: 'currentColor' }],
          ['path', { d: 'M4 18l5-5 3 3 3-4 5 6', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-linejoin': 'round' }]],
        ['span', { 'data-cdm-dyn-image-label': '' }, `${/logo/i.test(moniker) ? 'Logo' : 'Image'} · {{${moniker}}}`]]
    }
    return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, style ? { style } : {}, floating)]
  },
})

/** Whether an image node floats in the source document (schema v28 wrap / v31 anchor). */
export function isFloatingImage(attrs: Partial<ImageAttrs>): boolean {
  return attrs.wrap !== undefined && attrs.wrap !== TextBoxWrapEnum.Inline
}

/**
 * "Make inline": the one user choice that drops a floating picture's anchor. Acts on a selected
 * floating image only; returns whether it changed anything.
 */
export function makeImageInline(editor: { state: EditorState; view: { dispatch: (tr: Transaction) => void } }): boolean {
  const sel = editor.state.selection
  if (!(sel instanceof NodeSelection) || sel.node.type.name !== 'image' || !isFloatingImage(sel.node.attrs)) return false
  const tr = editor.state.tr.setNodeMarkup(sel.from, undefined, { ...sel.node.attrs, wrap: TextBoxWrapEnum.Inline, anchor: null })
  tr.setSelection(NodeSelection.create(tr.doc, sel.from))
  editor.view.dispatch(tr)
  return true
}

// ============ pageBreak / horizontalRule / toc ============

export const PageBreak = Node.create({
  name: 'pageBreak',
  group: 'block',
  atom: true,
  selectable: true,
  parseHTML() {
    return [{ tag: 'div[data-cdm-page-break]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-cdm-page-break': '' })]
  },
})

export { HorizontalRule }

export interface TocAttrs {
  content: DocBlock[]
}

/** Table of Contents preserved from source DOCX — not editable; content is a cached render. */
export const Toc = Node.create({
  name: 'toc',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return { content: { default: [] as DocBlock[], rendered: false } }
  },
  parseHTML() {
    return [{ tag: 'div[data-cdm-toc]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-cdm-toc': '' })]
  },
})

// ============ text box ============

export interface TextBoxAttrs {
  widthPt: number
  heightPt: number
  fillHex: string | null
  border: BorderSide | null
  wrap: TextBoxWrap
  /** Schema v32 — see TextBoxBlock.anchor. Carried untouched; cleared only by makeTextBoxInline. */
  anchor: FloatingAnchor | null
  vAlign: VAlign
  paddingLeftPt: number | null
  paddingRightPt: number | null
  paddingTopPt: number | null
  paddingBottomPt: number | null
  name: string | null
}

// Word's own bodyPr defaults, mirroring TextBoxBlock.DefaultHorizontalPaddingPt/Vertical in C#.
const TEXT_BOX_DEFAULT_H_PADDING_PT = 7.2
const TEXT_BOX_DEFAULT_V_PADDING_PT = 3.6

const BORDER_STYLE_CSS: Record<number, string> = {
  [BorderStyle.None]: 'none',
  [BorderStyle.Single]: 'solid',
  [BorderStyle.Double]: 'double',
  [BorderStyle.Dotted]: 'dotted',
  [BorderStyle.Dashed]: 'dashed',
  [BorderStyle.Thick]: 'solid',
}

/** The canvas styling for a text box, so the editor shows a box rather than bare paragraphs. */
function textBoxStyle(attrs: Partial<TextBoxAttrs>): string {
  const parts: string[] = []
  if (typeof attrs.widthPt === 'number' && attrs.widthPt > 0) parts.push(`width:${attrs.widthPt}pt`)
  // Height is deliberately a MINIMUM on the canvas, not a fixed height: the editor has to keep
  // showing text the author types past the box's stated size, where a renderer may clip it.
  if (typeof attrs.heightPt === 'number' && attrs.heightPt > 0) parts.push(`min-height:${attrs.heightPt}pt`)
  if (attrs.fillHex) parts.push(`background-color:#${attrs.fillHex}`)
  if (attrs.border && attrs.border.style !== BorderStyle.None)
    parts.push(`border:${attrs.border.widthPt}pt ${BORDER_STYLE_CSS[attrs.border.style] ?? 'solid'} #${attrs.border.colorHex}`)
  parts.push(
    `padding:${attrs.paddingTopPt ?? TEXT_BOX_DEFAULT_V_PADDING_PT}pt ` +
    `${attrs.paddingRightPt ?? TEXT_BOX_DEFAULT_H_PADDING_PT}pt ` +
    `${attrs.paddingBottomPt ?? TEXT_BOX_DEFAULT_V_PADDING_PT}pt ` +
    `${attrs.paddingLeftPt ?? TEXT_BOX_DEFAULT_H_PADDING_PT}pt`)
  if (attrs.vAlign === VAlignEnum.Center) parts.push('display:flex', 'flex-direction:column', 'justify-content:center')
  else if (attrs.vAlign === VAlignEnum.Bottom) parts.push('display:flex', 'flex-direction:column', 'justify-content:flex-end')
  return parts.join(';')
}

/**
 * A text box, editable in place. Unlike `toc` — a cached render, deliberately an atom — a text
 * box's content is authored content, so this is a real `block+` container and the author can type
 * in it. `isolating` keeps edits and selections from crossing the box's edge, the same way Loop
 * and Conditional do for their bodies.
 */
export const TextBox = Node.create({
  name: 'textBox',
  group: 'block',
  content: 'block+',
  isolating: true,
  addAttributes() {
    // `rendered: false` throughout, for the reason Section's comment gives — these drive output
    // and the style computed below, never a literal DOM attribute.
    return {
      widthPt: { default: 0, rendered: false },
      heightPt: { default: 0, rendered: false },
      fillHex: { default: null as string | null, rendered: false },
      border: { default: null as BorderSide | null, rendered: false },
      wrap: { default: TextBoxWrapEnum.Inline as TextBoxWrap, rendered: false },
      // Schema v32: the floating box's position — shown in the flow here, kept on save.
      anchor: { default: null as FloatingAnchor | null, rendered: false },
      vAlign: { default: VAlignEnum.Top as VAlign, rendered: false },
      paddingLeftPt: { default: null as number | null, rendered: false },
      paddingRightPt: { default: null as number | null, rendered: false },
      paddingTopPt: { default: null as number | null, rendered: false },
      paddingBottomPt: { default: null as number | null, rendered: false },
      name: { default: null as string | null, rendered: false },
    }
  },
  parseHTML() {
    return [{ tag: 'div[data-cdm-text-box]' }]
  },
  renderHTML({ HTMLAttributes, node }) {
    const style = textBoxStyle(node.attrs as Partial<TextBoxAttrs>)
    // The same marker a floating picture gets, so the author knows the output places it elsewhere.
    const floating = isFloatingTextBox(node.attrs as Partial<TextBoxAttrs>)
      ? { 'data-cdm-floating': '', title: 'Floating text box: its position and text wrap are kept on save. Put the cursor in it and choose "Make inline" to place it in the text.' }
      : {}
    return ['div', mergeAttributes(HTMLAttributes, { 'data-cdm-text-box': '', ...(style ? { style } : {}) }, floating), 0]
  },
})

/** Whether a text box floats in the source document (schema v15 wrap / v32 anchor). */
export function isFloatingTextBox(attrs: Partial<TextBoxAttrs>): boolean {
  return attrs.wrap !== undefined && attrs.wrap !== TextBoxWrapEnum.Inline
}

/** The innermost floating text box around the cursor: its position, or -1. */
export function floatingTextBoxPos(state: EditorState): number {
  const $from = state.selection.$from
  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d)
    if (node.type.name === 'textBox') return isFloatingTextBox(node.attrs) ? $from.before(d) : -1
  }
  return -1
}

/**
 * "Make inline" for a text box: drops the anchor of the floating box the cursor is in. A box is a
 * container the author types into, so it is found from the cursor rather than a node selection.
 * Returns whether it changed anything.
 */
export function makeTextBoxInline(editor: { state: EditorState; view: { dispatch: (tr: Transaction) => void } }): boolean {
  const pos = floatingTextBoxPos(editor.state)
  if (pos < 0) return false
  const node = editor.state.doc.nodeAt(pos)!
  editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, wrap: TextBoxWrapEnum.Inline, anchor: null }))
  return true
}

// ============ content control ============

export interface ContentControlAttrs {
  control: ContentControl
}

/** The CDM's own default control, so a node created without attrs is still a valid one. */
export const emptyContentControl = (): ContentControl => ({
  alias: null,
  tag: null,
  sourceId: null,
  type: ContentControlTypeEnum.RichText,
  lock: ContentControlLockEnum.None,
  dataBinding: null,
  placeholderDocPart: null,
  showingPlaceholder: false,
  temporary: false,
  multiLine: false,
  listItems: [],
  dateFormat: null,
  fullDate: null,
  checked: null,
})

/**
 * A block-level content control, editable in place like `textBox` rather than an atom like `toc`:
 * its content is authored content, and the whole point of keeping the control is that the author
 * can fill it in. `isolating` keeps edits from crossing the control's edge, the same way Loop,
 * Conditional and TextBox do.
 *
 * The dashed outline and the alias/tag label are what make the control visible at all — an
 * invisible wrapper is one an author deletes by accident.
 */
export const ContentControlNode = Node.create({
  name: 'contentControl',
  group: 'block',
  content: 'block+',
  isolating: true,
  addAttributes() {
    return { control: { default: emptyContentControl(), rendered: false } }
  },
  parseHTML() {
    return [{ tag: 'div[data-cdm-content-control]' }]
  },
  renderHTML({ HTMLAttributes, node }) {
    const control = node.attrs.control as ContentControl | undefined
    const label = control?.alias ?? control?.tag ?? ''
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-cdm-content-control': '',
      ...(label ? { 'data-label': label, title: label } : {}),
      style: 'border:1px dashed #9aa0a6;padding:2px',
    }), 0]
  },
})

/**
 * One end of an inline content control's range. An atom, like Bookmark and CommentMarker: zero-width
 * structure rather than editable content. Both markers are the same node type, distinguished by
 * `anchor`, so an author deleting one leaves a half-range the converters and renderers already
 * tolerate rather than a malformed document.
 */
export const ContentControlMarker = Node.create({
  name: 'contentControlMarker',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      controlId: { default: 0 },
      anchor: { default: ContentControlAnchorEnum.Start as ContentControlAnchor, rendered: false },
      control: { default: null as ContentControl | null, rendered: false },
    }
  },
  parseHTML() {
    return [{ tag: 'span[data-cdm-content-control-marker]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    const control = node.attrs.control as ContentControl | null
    return ['span', mergeAttributes(HTMLAttributes, {
      'data-cdm-content-control-marker': '',
      'data-anchor': String(node.attrs.anchor ?? ''),
      title: control?.alias ?? control?.tag ?? 'Content control',
    })]
  },
})

// ============ embedded objects ============

/**
 * A chart, SmartArt diagram or OLE object. An atom, and deliberately so: the CDM keeps it opaquely
 * (see EmbeddedObjectBlock), so the editor can move or delete it but has nothing to offer for editing
 * it — and an editor that let an author type into a chart would be promising something the model
 * cannot keep. Rendered as a labelled placeholder so the canvas shows that something is there.
 */
export const EmbeddedObject = Node.create({
  name: 'embeddedObject',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      kind: { default: 0, rendered: false },
      xml: { default: '', rendered: false },
      widthPt: { default: 0, rendered: false },
      heightPt: { default: 0, rendered: false },
      name: { default: null as string | null, rendered: false },
      previewBlobRef: { default: null as string | null, rendered: false },
      parts: { default: [] as unknown[], rendered: false },
    }
  },
  parseHTML() {
    return [{ tag: 'div[data-cdm-embedded-object]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    const kindLabel = ['Chart', 'SmartArt', 'Embedded object'][node.attrs.kind as number] ?? 'Embedded object'
    const label = (node.attrs.name as string | null) ?? kindLabel
    const height = (node.attrs.heightPt as number) || 120
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-cdm-embedded-object': '',
      style: `min-height:${height}pt;display:flex;align-items:center;justify-content:center;` +
             'border:1px dashed #9aa0a6;color:#5f6368',
    }), `${kindLabel}: ${label}`]
  },
})

// ============ loop / conditional / branch ============

export interface LoopAttrs {
  rawName: string
  normalizedKey: string
  itemAlias: string
  source: SourceLocation
}

export const Loop = Node.create({
  name: 'loop',
  group: 'block',
  content: 'block+',
  isolating: true,
  addAttributes() {
    return {
      rawName: { default: '' },
      normalizedKey: { default: '' },
      itemAlias: { default: 'item' },
      source: { default: defaultSourceLocation(), rendered: false },
    }
  },
  parseHTML() {
    return [{ tag: 'div[data-cdm-loop]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-cdm-loop': '' }), 0]
  },
})

export interface ConditionalAttrs {
  rawName: string
  normalizedKey: string
  negate: boolean
  source: SourceLocation
}

export const Conditional = Node.create({
  name: 'conditional',
  group: 'block',
  content: 'branch branch?',
  isolating: true,
  addAttributes() {
    return {
      rawName: { default: '' },
      normalizedKey: { default: '' },
      negate: { default: false },
      source: { default: defaultSourceLocation(), rendered: false },
    }
  },
  parseHTML() {
    return [{ tag: 'div[data-cdm-conditional]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-cdm-conditional': '' }), 0]
  },
})

export type BranchKind = 'body' | 'else'

export interface BranchAttrs {
  kind: BranchKind
}

/** A conditional's Body or ElseBody — only ever a child of `conditional`, never used standalone. */
export const Branch = Node.create({
  name: 'branch',
  content: 'block+',
  isolating: true,
  addAttributes() {
    return { kind: { default: 'body' as BranchKind } }
  },
  parseHTML() {
    return [{ tag: 'div[data-cdm-branch]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-cdm-branch': '' }), 0]
  },
})

// ============ inline atoms: placeholder / field / tab / hyperlink / footnote / endnote ============

export interface PlaceholderAttrs {
  rawName: string
  normalizedKey: string
  formatters: FormatterSpec[] | null
  format: RunFormat
  source: SourceLocation
}

/** A scalar variable placeholder: {{moniker}} or {{key | formatter:arg}}. */
export const Placeholder = Node.create({
  name: 'placeholder',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      rawName: { default: '' },
      normalizedKey: { default: '' },
      formatters: { default: null as FormatterSpec[] | null, rendered: false },
      format: { default: defaultRunFormat(), rendered: false },
      source: { default: defaultSourceLocation(), rendered: false },
    }
  },
  parseHTML() {
    return [{ tag: 'span[data-cdm-placeholder]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    // Placeholder's own chip styling (background/color, set in TemplateEditorPage.tsx's CSS) is
    // the primary visual treatment — layering the run's own format on top (bold/italic/color/etc)
    // still matters, since a placeholder sitting inside bold running text should look bold too.
    const style = runFormatToStyle((node.attrs.format as RunFormat | undefined) ?? defaultRunFormat())
    return ['span', mergeAttributes(HTMLAttributes, { 'data-cdm-placeholder': '', style }), placeholderLabel(node.attrs.rawName as string, node.attrs.formatters as FormatterSpec[] | null)]
  },

  // Typing a variable is the obvious way to write one, and until this existed it silently did not
  // work: `{{name}}` stayed a plain TextRun, so CanonicalMonikerScanner.ScanInlines (which matches
  // PlaceholderRun and nothing else) never saw it, the payload schema never listed it, and the
  // rendered document contained the literal text "{{name}}". Only the toolbar's Insert button
  // produced a real placeholder.
  addInputRules() {
    // Hand-written rather than Tiptap's nodeInputRule helper, for two reasons found by the tests
    // below: that helper leaves the `{{`/`}}` delimiters in the document alongside the new node,
    // and it has no way to DECLINE a match — returning null attrs still inserts a node, so
    // `{{ }}` and `{{#each items}}` would both become nonsense placeholders.
    return [
      new InputRule({
        // Anchored to the end of the typed text, which is what makes this fire on the closing
        // brace. The name excludes braces so an unterminated "{{" cannot swallow the next one,
        // and excludes the sentinel block for the same reason MonikerRx does server-side — a
        // sentinel between the braces would otherwise reach DiscoveredMoniker.RawName verbatim.
        find: /\{\{([^{}\uFFF0-\uFFF6]+)\}\}$/,
        handler: ({ range, match, chain }) => {
          const attrs = placeholderAttrs(match[1])
          if (attrs === null) return null   // not a variable — leave the typed text alone
          chain().deleteRange(range).insertContentAt(range.from, { type: this.name, attrs }).run()
        },
      }),
    ]
  },

  // Pasting is the other way a variable arrives as text — from a spec, an email, or another
  // template's exported source. Without this, paste would leave literal braces behind while
  // typing the same characters produced a chip.
  addPasteRules() {
    return [
      new PasteRule({
        find: /\{\{([^{}\uFFF0-\uFFF6]+)\}\}/g,
        handler: ({ range, match, chain }) => {
          const attrs = placeholderAttrs(match[1])
          if (attrs === null) return null
          chain().deleteRange(range).insertContentAt(range.from, { type: this.name, attrs }).run()
        },
      }),
    ]
  },
})

/**
 * The attrs for a placeholder built from a raw `{{name}}` spelling. Shared by the input rule, the
 * paste rule and CdmToolbar's Insert button so all three produce an identical node — a typed
 * variable and an inserted one must be indistinguishable in the CDM.
 *
 * Returns null (which suppresses the rule) in two cases:
 *
 * - a name that is only whitespace, since `{{ }}` is not a variable and a placeholder with an
 *   empty rawName would round-trip as an unnamed chip the user cannot see or select; and
 * - a **control tag** — `{{#each items}}`, `{{#if x}}`, `{{else}}`, `{{/each}}` and friends. These
 *   share the brace syntax but are not variables: a loop or conditional is a block node wrapping a
 *   range, which an inline input rule cannot build. Converting one would produce a placeholder
 *   literally named "#each items", which is worse than leaving the text alone — the toolbar's
 *   Loop/If buttons are what create those, and typed control tags stay text until then.
 */
function placeholderAttrs(rawName: string | undefined): Record<string, unknown> | null {
  const trimmed = rawName?.trim() ?? ''
  if (trimmed.length === 0) return null
  if (trimmed.startsWith('#') || trimmed.startsWith('/') || trimmed === 'else') return null
  return {
    rawName: trimmed,
    normalizedKey: normalizeMonikerKey(trimmed),
    formatters: null,
    format: defaultRunFormat(),
    source: defaultSourceLocation(),
  }
}

export interface FieldAttrs {
  fieldType: FieldType
  staticText: string
  format: RunFormat
  instruction: string | null
}

/** A DOCX field (PAGE, NUMPAGES, etc). Atom, since a field is not editable free text. */
export const Field = Node.create({
  name: 'field',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      fieldType: { default: 0 as FieldType },
      staticText: { default: '' },
      format: { default: defaultRunFormat(), rendered: false },
      // rendered: false — the field code drives output formats, never the canvas, which shows
      // the cached result. Without the attr the editor would drop it on the first save.
      instruction: { default: null, rendered: false },
    }
  },
  parseHTML() {
    return [{ tag: 'span[data-cdm-field]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    const style = runFormatToStyle((node.attrs.format as RunFormat | undefined) ?? defaultRunFormat())
    return ['span', mergeAttributes(HTMLAttributes, { 'data-cdm-field': '', style }), String(node.attrs.staticText || '')]
  },
})

/** TabRun — an explicit tab character inside a paragraph, distinct from the browser Tab key. */
export const TabStop = Node.create({
  name: 'tabStop',
  group: 'inline',
  inline: true,
  atom: true,
  parseHTML() {
    return [{ tag: 'span[data-cdm-tab]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-cdm-tab': '' }), '\t']
  },
})

export interface BookmarkAttrs {
  name: string
}

/**
 * A named bookmark target. Rendered as an empty anchor carrying the name as an `id`, which is
 * both what the HTML renderer emits and what makes the editor canvas a valid link target.
 * `name` is a rendered attr, unlike the output-only attrs elsewhere: it IS the node's identity.
 */
export const Bookmark = Node.create({
  name: 'bookmark',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return { name: { default: '' } }
  },
  parseHTML() {
    return [{ tag: 'a[data-cdm-bookmark]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['a', mergeAttributes(HTMLAttributes, { 'data-cdm-bookmark': '', id: String(node.attrs.name ?? '') })]
  },
})

/**
 * An equation. An atom: the CDM keeps the real markup opaquely (see EquationRun) and the editor has
 * no equation editor to offer, so it can be moved and deleted but not typed into — the alternative
 * would be letting an author edit the approximation and silently losing the equation it stands for.
 */
export const EquationRunNode = Node.create({
  name: 'equation',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      xml: { default: '', rendered: false },
      text: { default: '' },
      isDisplay: { default: false, rendered: false },
      format: { default: defaultRunFormat(), rendered: false },
    }
  },
  parseHTML() {
    return [{ tag: 'span[data-cdm-equation]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, {
      'data-cdm-equation': '',
      style: 'font-family:Cambria Math,serif;background:rgba(0,0,0,0.04);padding:0 2px;border-radius:2px',
      title: 'Equation — edit in Word',
    }), String(node.attrs.text ?? '')]
  },
})

/** The chip label for an inline conditional: "if title: Dr. {{title}} | else: Customer". */
export function conditionalRunLabel(run: ConditionalRun): string {
  const text = (inlines: DocInline[]): string => inlines.map(i =>
    i.$type === 'run' ? i.text
      : i.$type === 'placeholder' ? `{{${i.rawName}}}`
        : i.$type === 'conditionalRun' ? `[${conditionalRunLabel(i)}]`
          : i.$type === 'loopRun' ? `[${loopRunLabel(i)}]`
            : '').join('').trim()
  const head = `${run.negate ? 'unless' : 'if'} ${run.rawName}: ${text(run.inlines)}`
  return run.elseInlines ? `${head} | else: ${text(run.elseInlines)}` : head
}

/**
 * An inline (mid-paragraph) conditional — ConditionalRun, schema v34. An atom holding the node's
 * JSON verbatim, so it round-trips losslessly; the chip shows the condition and both branches.
 * Still an atom rather than a container inline (which would need its own selection and content
 * model): clicking it opens ConditionalRunPopover, which edits the branches as template text
 * (cdm/conditionalRunEdit.ts).
 */
export const ConditionalRunNode = Node.create({
  name: 'conditionalRun',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return { json: { default: null as unknown as ConditionalRun, rendered: false } }
  },
  parseHTML() {
    return [{ tag: 'span[data-cdm-conditional-run]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    const run = node.attrs.json as ConditionalRun | null
    return ['span', mergeAttributes(HTMLAttributes, {
      'data-cdm-conditional-run': '',
      style: 'background:rgba(124,58,237,0.08);border:1px dashed rgba(124,58,237,0.5);padding:0 3px;border-radius:3px',
      title: 'Inline condition — click to edit',
    }), run ? conditionalRunLabel(run) : '']
  },
})

/** The chip label for an inline loop: "each tags: {{name}}, ". */
export function loopRunLabel(run: LoopRun): string {
  const text = run.inlines.map(i =>
    i.$type === 'run' ? i.text
      : i.$type === 'placeholder' ? `{{${i.rawName}}}`
        : i.$type === 'conditionalRun' ? `[${conditionalRunLabel(i)}]`
          : i.$type === 'loopRun' ? `[${loopRunLabel(i)}]`
            : '').join('')
  return `each ${run.rawName}${run.itemAlias && run.itemAlias !== 'item' ? ` as ${run.itemAlias}` : ''}: ${text}`
}

/**
 * An inline (mid-paragraph) loop — LoopRun, schema v35. Like ConditionalRunNode, an atom holding the node's JSON
 * verbatim so it round-trips losslessly; the chip shows the collection and the repeated text. Editing the repeated
 * text happens in the source template.
 */
export const LoopRunNode = Node.create({
  name: 'loopRun',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return { json: { default: null as unknown as LoopRun, rendered: false } }
  },
  parseHTML() {
    return [{ tag: 'span[data-cdm-loop-run]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    const run = node.attrs.json as LoopRun | null
    return ['span', mergeAttributes(HTMLAttributes, {
      'data-cdm-loop-run': '',
      style: 'background:rgba(13,148,136,0.08);border:1px dashed rgba(13,148,136,0.6);padding:0 3px;border-radius:3px',
      title: 'Inline loop — repeats for each item',
    }), run ? loopRunLabel(run) : '']
  },
})

/**
 * Text a reviewer deleted under tracked changes. An atom: it is not part of the document's text flow,
 * and an author editing around it must not be able to type inside it. Rendered struck through so the
 * canvas shows what Word shows.
 */
export const DeletedRunNode = Node.create({
  name: 'deleted',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      text: { default: '' },
      format: { default: defaultRunFormat(), rendered: false },
      revision: { default: null as Revision | null, rendered: false },
    }
  },
  parseHTML() {
    return [{ tag: 'del[data-cdm-deleted]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    const revision = node.attrs.revision as Revision | null
    return ['del', mergeAttributes(HTMLAttributes, {
      'data-cdm-deleted': '',
      title: revision?.author ? `Deleted by ${revision.author}` : 'Deleted',
    }), String(node.attrs.text ?? '')]
  },
})

/**
 * A comment marker. An atom, like Bookmark: it is zero-width structure, not editable content —
 * the comment's body lives on the document, not in the text flow.
 *
 * Rendered as a thin coloured tick so the author can see where a comment is anchored. Without
 * something visible, a marker would be impossible to place or delete deliberately.
 */
export const CommentMarker = Node.create({
  name: 'comment',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      commentId: { default: 0 },
      anchor: { default: CommentAnchorEnum.Reference as CommentAnchor, rendered: false },
    }
  },
  parseHTML() {
    return [{ tag: 'span[data-cdm-comment]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, {
      'data-cdm-comment': '',
      'data-anchor': String(node.attrs.anchor ?? ''),
      title: `Comment ${node.attrs.commentId}`,
    })]
  },
})

export interface HyperlinkAttrs {
  text: string
  url: string | null
  anchorName: string | null
  format: RunFormat
}

/**
 * A hyperlink, modeled as an inline ATOM rather than a Tiptap mark. CDM's HyperlinkRun is
 * genuinely a leaf (one Text + one RunFormat, never a span of independently-marked-up runs — see
 * its doc comment in DocInline.cs), so a mark (which can straddle several differently-formatted
 * text nodes) would let the editor produce content this schema cannot round-trip losslessly. An
 * atom guarantees the round trip stays exact.
 */
export const HyperlinkRun = Node.create({
  name: 'hyperlinkRun',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      text: { default: '' },
      url: { default: null as string | null },
      anchorName: { default: null as string | null },
      format: { default: defaultRunFormat(), rendered: false },
    }
  },
  parseHTML() {
    return [{ tag: 'a[data-cdm-hyperlink]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    const href = (node.attrs.url as string | null) ?? (node.attrs.anchorName ? `#${node.attrs.anchorName}` : undefined)
    const style = runFormatToStyle((node.attrs.format as RunFormat | undefined) ?? defaultRunFormat())
    return ['a', mergeAttributes(HTMLAttributes, { 'data-cdm-hyperlink': '', href, style }), String(node.attrs.text || '')]
  },
})

export interface FootnoteRefAttrs {
  footnoteId: number
}

export const FootnoteRef = Node.create({
  name: 'footnoteRef',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return { footnoteId: { default: 0 } }
  },
  parseHTML() {
    return [{ tag: 'sup[data-cdm-footnote-ref]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['sup', mergeAttributes(HTMLAttributes, { 'data-cdm-footnote-ref': '' }), String(node.attrs.footnoteId)]
  },
})

export interface EndnoteRefAttrs {
  endnoteId: number
}

export const EndnoteRef = Node.create({
  name: 'endnoteRef',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return { endnoteId: { default: 0 } }
  },
  parseHTML() {
    return [{ tag: 'sup[data-cdm-endnote-ref]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['sup', mergeAttributes(HTMLAttributes, { 'data-cdm-endnote-ref': '' }), String(node.attrs.endnoteId)]
  },
})

// ============ unknown-node fallback (round-trip safety net) ============
//
// A $type this editor build doesn't recognize (e.g. a CDM written by a newer backend) becomes one
// of these instead of being dropped — protecting against exactly the failure mode a versioned
// schema migration wouldn't help with: the editor cannot migrate a property it has never heard of.

export interface UnknownBlockAttrs {
  json: DocBlock
}

export const UnknownBlock = Node.create({
  name: 'unknownBlock',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return { json: { default: null as unknown as DocBlock, rendered: false } }
  },
  parseHTML() {
    return [{ tag: 'div[data-cdm-unknown-block]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    const type = (node.attrs.json as { $type?: string } | null)?.$type ?? 'unknown'
    return ['div', mergeAttributes(HTMLAttributes, { 'data-cdm-unknown-block': '' }), `[Unsupported block: ${type}]`]
  },
})

export interface UnknownInlineAttrs {
  json: DocInline
}

export const UnknownInline = Node.create({
  name: 'unknownInline',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return { json: { default: null as unknown as DocInline, rendered: false } }
  },
  parseHTML() {
    return [{ tag: 'span[data-cdm-unknown-inline]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    const type = (node.attrs.json as { $type?: string } | null)?.$type ?? 'unknown'
    return ['span', mergeAttributes(HTMLAttributes, { 'data-cdm-unknown-inline': '' }), `[Unsupported: ${type}]`]
  },
})

// ============ hardBreak (LineBreakRun) ============

export { HardBreak }

// ============ runFormat mark ============
//
// A single mark carrying the entire RunFormat object as one attr, applied to `text` nodes for
// TextRun. See the file-level doc comment for why this isn't decomposed into per-property marks.

export const CdmRunFormat = Mark.create({
  name: 'runFormat',
  addAttributes() {
    return { format: { default: defaultRunFormat(), rendered: false } }
  },
  parseHTML() {
    return [{ tag: 'span[data-cdm-run]' }]
  },
  renderHTML({ mark, HTMLAttributes }) {
    const style = runFormatToStyle((mark.attrs.format as RunFormat | undefined) ?? defaultRunFormat())
    return ['span', mergeAttributes(HTMLAttributes, { 'data-cdm-run': '', style }), 0]
  },
})

// ============ extension list ============

export const cdmExtensions: Extensions = [
  CdmDocument,
  Text,
  Section,
  Paragraph,
  Heading,
  CdmList,
  CdmListItem,
  CdmTable,
  CdmTableRow,
  CdmTableCell,
  CdmTableHeader,
  CdmImage,
  PageBreak,
  HorizontalRule,
  TextBox,
  Toc,
  ContentControlNode,
  ContentControlMarker,
  EmbeddedObject,
  Loop,
  Conditional,
  Branch,
  Placeholder,
  Field,
  TabStop,
  Bookmark,
  CommentMarker,
  DeletedRunNode,
  EquationRunNode,
  ConditionalRunNode,
  LoopRunNode,
  HyperlinkRun,
  FootnoteRef,
  EndnoteRef,
  UnknownBlock,
  UnknownInline,
  HardBreak,
  CdmRunFormat,
  UndoRedo,
]
